import React from 'react';
import { AlignedInfo } from '@common/components';
import { Typography } from 'antd';
import { WorkforceGroupByIdQueryCgResponse } from '@common/graphql/schema';
import { convertToType } from '@scheduling/services';
import type { WorkforceGroupUtilizationRule } from '@common/graphql';

const { Text } = Typography;

type Props = {
  data: WorkforceGroupByIdQueryCgResponse;
};

function humanizeHour(hours: number) {
  return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
}

const UTILIZATION_RULE_LABELS: Record<WorkforceGroupUtilizationRule, string> = {
  UNRESTRICTED: 'No rules enforced',
  WEEKLY: 'Fixed Week(s) (Saturday \u2013 Sunday)',
  ROLLING_WINDOW: 'Rolling 7-day',
};

export function AdminWorkforceGroupDetails({ data }: Props) {
  const { workforceGroup } = data;

  const renderFieldLabel = (label: string) => (
    <div style={{ width: 200 }}>{label}</div>
  );

  const renderDistributionValue = (
    enabled: boolean,
    value: string,
    valueType: 'days' | 'hours'
  ) => (
    <Text>
      {enabled ? 'Yes' : 'No'}{' '}
      {enabled
        ? `- ${convertToType(value, 'hours', valueType)} ${valueType}`
        : ''}
    </Text>
  );

  const hasConsecutiveHoursLimit = workforceGroup.consecutiveHoursLimit > 0;

  const row = (
    label: string,
    value: React.ReactNode
  ): [React.ReactNode, React.ReactNode] => [renderFieldLabel(label), value];

  const consecutiveHoursRules: [React.ReactNode, React.ReactNode][] = [
    row(
      'Limit consecutive working hours',
      hasConsecutiveHoursLimit
        ? `Yes - ${humanizeHour(workforceGroup.consecutiveHoursLimit)}`
        : 'No'
    ),
    ...(hasConsecutiveHoursLimit
      ? [
          row(
            'Bridge gaps up to',
            humanizeHour(workforceGroup.consecutiveHoursBridgeTolerance)
          ),
          row(
            'Minimum rest to reset',
            humanizeHour(workforceGroup.consecutiveHoursResetPeriod)
          ),
        ]
      : []),
  ];

  const weeklyUtilizationRules = [
    row(
      'Rule',
      UTILIZATION_RULE_LABELS[workforceGroup.utilizationRule] ??
        workforceGroup.utilizationRule
    ),
    row('Minimum', humanizeHour(workforceGroup.weeklyMinimumHours)),
    row('Maximum', humanizeHour(workforceGroup.weeklyMaximumHours)),
  ];

  const shiftDistributionRules = [
    row(
      'Limit shift visibility',
      renderDistributionValue(
        workforceGroup.defaultConfig?.gateEarliestAdvanceTime || false,
        String(workforceGroup.defaultConfig?.earliestAdvanceTime),
        workforceGroup.defaultConfig?.earliestAdvanceTimeFormat as
          | 'days'
          | 'hours'
      )
    ),
    row(
      'Add delay to schedule release',
      renderDistributionValue(
        workforceGroup.defaultConfig?.gateOpenShiftsVisibility || false,
        String(workforceGroup.defaultConfig?.openShiftsVisibilityDelayHours),
        workforceGroup.defaultConfig?.openShiftsVisibilityDelayHoursFormat as
          | 'days'
          | 'hours'
      )
    ),
  ];

  return (
    <>
      <div className="admin-workforce-group-details">
        <Text strong className="admin-workforce-group-details__title">
          Consecutive Hours
        </Text>
        <div style={{ height: 16 }} />
        <AlignedInfo content={consecutiveHoursRules} />
        <div style={{ height: 12 }} />
        <Text strong className="admin-workforce-group-details__title">
          Weekly Utilization
        </Text>
        <div style={{ height: 16 }} />
        <AlignedInfo content={weeklyUtilizationRules} />
        <div style={{ height: 12 }} />
        <Text strong className="admin-workforce-group-details__title">
          Shift Distribution
        </Text>
        <div style={{ height: 16 }} />
        <AlignedInfo content={shiftDistributionRules} />
      </div>
      <style jsx>{`
        .admin-workforce-group-details {
          margin-top: 16px;
        }
        :global(.admin-workforce-group-details__title) {
          font-size: 16px;
        }
      `}</style>
    </>
  );
}
